import React from 'react';
import styled from 'styled-components';
import WidthContainer from '../WidthContainer';

const TermsContainer = styled.div`
  padding: 40px 0;
  color: ${({ theme }) => theme.text.regular};
`;

const TermsTitle = styled.h1`
  font-size: 32px;
  margin-bottom: 24px;
  font-weight: 700;
`;

const TermsDate = styled.p`
  font-size: 16px;
  margin-bottom: 24px;
  color: ${({ theme }) => theme.text.light};
`;

const TermsSection = styled.div`
  margin-bottom: 24px;
`;

const TermsHeading = styled.h2`
  font-size: 24px;
  margin-bottom: 16px;
  font-weight: 600;
`;

const TermsParagraph = styled.p`
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 16px;
`;

const TermsList = styled.ul`
  margin-bottom: 16px;
  padding-left: 24px;
`;

const TermsListItem = styled.li`
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 8px;
`;

const TermsWarning = styled.div`
  background-color: ${({ theme }) => theme.text.primary}3cd;
  border: 1px solid ${({ theme }) => theme.status.warning.background};
  color: ${({ theme }) => theme.status.warning.primary};
  padding: 16px;
  border-radius: 4px;
  margin-bottom: 16px;
`;

const TermsOfUse = () => {
  return (
    <WidthContainer>
      <TermsContainer>
        <TermsTitle>Terms of Use</TermsTitle>
        <TermsDate>Effective Date: March 17, 2025</TermsDate>

        <TermsSection>
          <TermsParagraph>
            Welcome to Deks! These Terms of Use ("Terms") constitute a binding
            legal agreement between you and DEKSxyz, a Wyoming-based limited
            liability company ("Deks," "we," "our," or "us"). These Terms govern
            your use of our services, including our website, application, and
            any associated interfaces or platforms (collectively, the
            "Services").
          </TermsParagraph>

          <TermsParagraph>
            By accessing or using our Services, including visiting our website,
            connecting a Privy wallet, or interacting with decentralized
            exchange services through our interface, you represent and agree
            that:
          </TermsParagraph>

          <TermsList>
            <TermsListItem>
              You have read, understood, and agree to be bound by these Terms.
            </TermsListItem>
            <TermsListItem>
              You are of legal age and have the legal capacity to form a binding
              contract.
            </TermsListItem>
            <TermsListItem>
              You have the authority to enter into these Terms.
            </TermsListItem>
          </TermsList>

          <TermsWarning>
            IF YOU DO NOT AGREE WITH THESE TERMS, DO NOT ACCESS OR USE THE
            SERVICES.
          </TermsWarning>
        </TermsSection>

        <TermsSection>
          <TermsHeading>OUR SERVICES</TermsHeading>
          <TermsParagraph>
            Deks provides a user-friendly interface to interact with
            decentralized exchanges and protocols ("Third-Party Protocols") such
            as Uniswap, Raydium, and other decentralized platforms ("Third-Party
            Services"). We enable users to connect their programmable Wallet
            provided by Privy.io or other compatible wallets ("Wallets") to
            these decentralized protocols. Deks assists in creating and
            submitting transactions via these Wallets but does not control or
            process transactions independently from your authorization.
          </TermsParagraph>
        </TermsSection>

        <TermsSection>
          <TermsHeading>YOUR WALLET</TermsHeading>
          <TermsParagraph>
            Your use of our Services requires connecting a Wallet provided by
            Privy.io or another third-party service provider. You retain full
            custody and control over your Wallet and digital assets. Deks does
            not store or have direct access to your private keys, passphrases,
            or passwords associated with your Wallet.
          </TermsParagraph>

          <TermsWarning>
            IMPORTANT: If you lose your private key or Wallet credentials,
            neither Deks nor Privy can recover them for you.
          </TermsWarning>
        </TermsSection>

        <TermsSection>
          <TermsHeading>TRANSACTIONS AND RISKS</TermsHeading>
          <TermsParagraph>
            Transactions executed via decentralized exchanges (DEXs) and
            blockchains are irreversible and final. You expressly acknowledge
            and agree that:
          </TermsParagraph>

          <TermsList>
            <TermsListItem>
              You are solely responsible for reviewing and authorizing all
              transactions you submit via your Wallet.
            </TermsListItem>
            <TermsListItem>
              Transactions initiated through our interface, once authorized by
              you, cannot be reversed, modified, or canceled by Deks.
            </TermsListItem>
            <TermsListItem>
              Blockchain transactions are subject to network confirmation and
              validation, which may result in delays or failures beyond Deks's
              control.
            </TermsListItem>
          </TermsList>
        </TermsSection>

        <TermsSection>
          <TermsHeading>DISCLAIMERS</TermsHeading>
          <TermsParagraph>
            Deks is not a broker, financial advisor, custodian, or intermediary.
            We do not provide financial or investment advice, recommendations,
            or endorsements of particular digital assets or transactions. You
            acknowledge and agree that:
          </TermsParagraph>

          <TermsList>
            <TermsListItem>
              You assume all risks associated with using decentralized exchanges
              and blockchain technology.
            </TermsListItem>
            <TermsListItem>
              Digital assets and cryptocurrencies are highly volatile, and you
              should independently evaluate risks before transactions.
            </TermsListItem>
          </TermsList>
        </TermsSection>

        <TermsSection>
          <TermsHeading>FEES</TermsHeading>
          <TermsParagraph>
            Deks charges a variable fee for each transaction initiated through
            our interface. This fee consists of:
          </TermsParagraph>

          <TermsList>
            <TermsListItem>
              A fixed component retained by Deks for providing the service.
            </TermsListItem>
            <TermsListItem>
              A variable component used to cover blockchain transaction fees
              ("Gas Fees").
            </TermsListItem>
          </TermsList>

          <TermsParagraph>
            Although Deks sponsors the Gas Fees upfront, these fees are
            ultimately paid by you at the time of the transaction and included
            within the total transaction fee. Gas Fees fluctuate according to
            network conditions and are not controlled by Deks.
          </TermsParagraph>
        </TermsSection>

        <TermsSection>
          <TermsHeading>THIRD-PARTY SERVICES</TermsHeading>
          <TermsParagraph>
            Our Services provide interfaces connecting you to decentralized
            exchanges and protocols operated by third parties (e.g., Uniswap,
            Raydium). Deks does not:
          </TermsParagraph>

          <TermsList>
            <TermsListItem>
              Own, operate, or control third-party protocols.
            </TermsListItem>
            <TermsListItem>
              Guarantee the functionality, security, or reliability of
              third-party protocols.
            </TermsListItem>
            <TermsListItem>
              Assume responsibility for errors, losses, or damages arising from
              third-party services.
            </TermsListItem>
          </TermsList>

          <TermsParagraph>
            You must adhere to the terms and conditions provided by third-party
            service providers.
          </TermsParagraph>
        </TermsSection>

        <TermsSection>
          <TermsHeading>USER RESPONSIBILITIES</TermsHeading>
          <TermsParagraph>
            You represent and warrant that you will:
          </TermsParagraph>

          <TermsList>
            <TermsListItem>
              Use the Services only for lawful purposes.
            </TermsListItem>
            <TermsListItem>
              Not use the Services for money laundering, terrorist financing, or
              illicit activities.
            </TermsListItem>
            <TermsListItem>
              Not attempt to disrupt or interfere with the Services or
              third-party protocols.
            </TermsListItem>
          </TermsList>
        </TermsSection>

        <TermsSection>
          <TermsHeading>DISCLAIMER OF WARRANTIES</TermsHeading>
          <TermsParagraph>
            THE SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS
            WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. DEKS
            EXPRESSLY DISCLAIMS ALL WARRANTIES INCLUDING, BUT NOT LIMITED TO,
            WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
            NON-INFRINGEMENT, OR THAT THE SERVICES WILL BE UNINTERRUPTED OR
            ERROR-FREE.
          </TermsParagraph>
        </TermsSection>

        <TermsSection>
          <TermsHeading>LIMITATION OF LIABILITY</TermsHeading>
          <TermsParagraph>
            To the fullest extent permitted by law, Deks will not be liable for
            any direct, indirect, incidental, special, consequential, or
            exemplary damages arising from your use or inability to use the
            Services or related to any transactions executed through the
            Services.
          </TermsParagraph>

          <TermsParagraph>
            Deks's total aggregate liability shall not exceed $100 or the amount
            of fees (if any) you paid to Deks in the preceding 12 months,
            whichever is greater.
          </TermsParagraph>
        </TermsSection>

        <TermsSection>
          <TermsHeading>INDEMNIFICATION</TermsHeading>
          <TermsParagraph>
            You agree to indemnify and hold harmless Deks from any claims,
            liabilities, damages, and expenses (including reasonable attorneys'
            fees) arising out of or relating to your use of the Services or any
            violation of these Terms.
          </TermsParagraph>
        </TermsSection>

        <TermsSection>
          <TermsHeading>GOVERNING LAW AND DISPUTES</TermsHeading>
          <TermsParagraph>
            These Terms are governed by and construed in accordance with the
            laws of the State of Wyoming, without regard to its conflict of law
            principles.
          </TermsParagraph>

          <TermsParagraph>
            All disputes arising under these Terms will be settled by
            arbitration administered by the American Arbitration Association
            under its Commercial Arbitration Rules. Arbitration proceedings will
            occur in Wyoming, and both parties waive rights to class actions and
            jury trials.
          </TermsParagraph>
        </TermsSection>

        <TermsSection>
          <TermsHeading>MODIFICATIONS</TermsHeading>
          <TermsParagraph>
            We reserve the right to update or modify these Terms at our sole
            discretion. Any changes will become effective immediately upon
            posting. Continued use of the Services constitutes acceptance of the
            revised Terms.
          </TermsParagraph>
        </TermsSection>

        <TermsSection>
          <TermsHeading>CONTACT</TermsHeading>
          <TermsParagraph>
            For questions, feedback, or issues related to the Services, contact
            us at info@deks.xyz.
          </TermsParagraph>
        </TermsSection>

        <TermsSection>
          <TermsHeading>GENERAL</TermsHeading>
          <TermsList>
            <TermsListItem>
              You may not assign or transfer your rights under these Terms
              without prior written consent from Deks.
            </TermsListItem>
            <TermsListItem>
              If any provision of these Terms is deemed unenforceable, the
              remaining provisions will remain valid.
            </TermsListItem>
            <TermsListItem>
              These Terms constitute the entire agreement between you and Deks
              relating to your use of the Services and supersede all prior
              agreements and understandings.
            </TermsListItem>
          </TermsList>

          <TermsParagraph>
            By accessing and using Deks, you acknowledge that you have read,
            understand, and agree to these Terms of Use.
          </TermsParagraph>
        </TermsSection>
      </TermsContainer>
    </WidthContainer>
  );
};

export default TermsOfUse;
